import { filter, flow, get, isEmpty, map, sortBy, uniqBy } from "lodash";
import moment from "moment";
import { HistoryTaskData } from "core/queries/GetHistoryTasks";
import { UserData as LoggedInUserData } from "core/queries/GetLoggedInUser";
import {
  GetUserNotificationResourceCleanActivityCleaningRequest,
  GetUserNotificationTask,
} from "core/queries/GetNotifications";
import { TaskSubjectType } from "core/queries/GetTask";
import { TaskData } from "core/queries/GetTasks";
import { UserData } from "core/queries/GetUsers";
import { normalizeType } from "tasks/helpers";
import { DispatchRoleName, TypeEnum } from "typings/globalGQLTypes";

export const GET_TEAM_ROLES = [
  "admin",
  "get_overnight_shift_lead",
  "get_team_lead",
  "guest_experience_lead",
  "guest_experience_specialist",
];

export const LEAD_OR_MANAGER_ROLES = ["tom_manager", "tom_team_lead"];
// While this is a duplicate of LEAD_OR_MANAGER_ROLES right now, I think keeping them separate is useful if one changes.
export const CLEANING_MANAGER_ROLES = [
  "cleaning_admin",
  "tom_manager",
  "tom_team_lead",
];

export const TOM_TEAM_ROLES = [
  "admin",
  "tom_configuration_admin",
  "tom_manager",
  "tom_team_lead",
];

// `isTomMember` is starting to become too much of an "umbrellla", as `admin` was given out like candy.
// Keep in sync with `dispatch_dashboard_admin?` here: https://github.com/Flatbook/flatbook/blob/main/app/models/concerns/user_role_concern.rb#L24-L26
export const DISPATCH_ADMIN_ROLES = ["tom_manager", "tom_team_lead"];

export const HK_CONTRACTOR_ROLES = [
  "hk_contractor",
  "housekeeping",
  "housekeeper",
];

const PTE_TASK_TYPES = [
  "GuestIssue",
  "GuestRequest",
  "Other",
  "QuantityGuestRequest",
];

export const isGETMember = (user: UserData) =>
  GET_TEAM_ROLES.some(role => user.roles && user.roles.includes(role));
export const isLeadOrManager = (user: UserData) =>
  LEAD_OR_MANAGER_ROLES.some(role => user.roles && user.roles.includes(role));
export const isCleaningManager = (user: UserData) =>
  CLEANING_MANAGER_ROLES.some(role => user.roles && user.roles.includes(role));
export const isTOMMember = (user: UserData) =>
  TOM_TEAM_ROLES.some(role => user.roles && user.roles.includes(role));
export const isDispatchDashboardAdmin = (user: UserData) =>
  DISPATCH_ADMIN_ROLES.some(role => user.roles && user.roles.includes(role));
export const isHKManager = (user: UserData) =>
  user.dispatchRoles?.some(role => role.name === DispatchRoleName.hk_manager) ||
  false;
export const isHKContractor = (user: UserData) =>
  [
    !isGETMember(user),
    !isTOMMember(user),
    HK_CONTRACTOR_ROLES.some(role => user.roles?.includes(role)),
  ].every(Boolean);

export enum OccupancyState {
  Vacant = "VACANT",
  Occupied = "OCCUPIED",
  DuringStay = "DURING_STAY",
}

export const OccupancyStatus = {
  [OccupancyState.Vacant]: "Vacant",
  [OccupancyState.Occupied]: "Occupied",
  [OccupancyState.DuringStay]: "During Stay",
};

export const mapUserDataToProps = (
  user: UserData | LoggedInUserData,
): User => ({
  avatarUrl: user.avatar && user.avatar.url,
  cityId: user?.tomTeamMember?.city?.id || null,
  cleaningCompany: user.cleaningCompany || null,
  dispatchRoles: user.dispatchRoles,
  email: user.email,
  firstName: user.firstName,
  id: user.id,
  isCleaningManager: isCleaningManager(user),
  isDispatchDashboardAdmin: isDispatchDashboardAdmin(user),
  isGETMember: isGETMember(user),
  isHKContractor: isHKContractor(user),
  isHKManager: isHKManager(user),
  isLeadOrManager: isLeadOrManager(user),
  isTOMMember: isTOMMember(user),
  lastName: user.lastName,
  name: user.name || `${user.firstName} ${user.lastName}`,
  roles: user.roles || [],
  tags: user.tags || [],
  timezone: user?.tomTeamMember?.city?.timezone || null,
  featureFlags: "featureFlags" in user ? user.featureFlags : [],
});

export const getTaskId = ({ id }: TaskData) => id;
export const getTaskAsanaUrl = ({ asanaUrl }: TaskData) => asanaUrl;
export const getTaskAssignees = (task: TaskData): User[] => {
  const assignee = get(task, "assignee", null);
  const assignees = (get(task, "assignees", null) || []).filter(
    x => x.id !== assignee?.id,
  );

  return ([assignee, ...assignees] as unknown as User[]).filter(x => !!x);
};
export const getTaskAssigneeIds = (task: TaskData): string[] => {
  return getTaskAssignees(task).map(assignee => assignee.id);
};
export const getTaskBookingInfo = (task: TaskData) => {
  return task.unit?.bookingInfo;
};
export const getTaskCityId = (task: TaskData) => {
  return task.city?.id;
};
export const getTaskAssigneeNames = (task: TaskData, limit = 1): string => {
  const names = getTaskAssignees(task).map(
    assignee => assignee.name || `${assignee.firstName} ${assignee.lastName}`,
  );
  const shown = names.slice(0, limit);
  const hidden = names.slice(limit);
  return (
    (shown.length > 0 &&
      shown
        .concat(hidden.length > 0 ? `+${hidden.length} more` : "")
        .filter(x => !!x)
        .join(", ")) ||
    "No Assignee"
  );
};
export const getTaskDescription = ({ name }: TaskData) =>
  name || "(No task name)";
export const getTaskInternalRef = (
  task: TaskData | HistoryTaskData | GetUserNotificationTask,
) => {
  const normalizedTaskType = normalizeType(task.type);
  switch (normalizedTaskType) {
    case TypeEnum.Tom_Tasks_SpaceClean:
      return task?.cufSpace?.internalRef || "(Space task)";
    default:
      if (task.subjectType === "Building") {
        return task?.building?.shortInternalRef || "(Building task)";
      }
      return task?.unit?.shortInternalRef || "(City task)";
  }
};
export const getCleaninRequestInternalRef = (
  cleaningRequest: GetUserNotificationResourceCleanActivityCleaningRequest,
) => {
  const { space, unit } = cleaningRequest;
  if (unit) {
    return unit.shortInternalRef;
  }
  if (space) {
    return space.internalRef;
  }
  return "";
};
export const getTaskFutureCheckOuts = (task: TaskData) => {
  const now = moment();
  const isVacantAllDay = get(task, "unit.checkInCheckOutInfo.isVacantAllDay");
  if (isVacantAllDay) {
    return [now];
  }
  return flow(
    dates => map(dates, date => moment(get(task, date))),
    dates => uniqBy(dates, date => date.valueOf()),
    dates => filter(dates, date => date.isAfter(now)),
    dates => sortBy(dates, date => date.diff(now)),
  )([
    "unit.checkInCheckOutInfo.checkOutToday.checkOutTime",
    "unit.checkInCheckOutInfo.checkInToday.checkOutTime",
    "unit.checkInCheckOutInfo.occupiedByToday.checkOutTime",
  ]);
};
export const getTaskCompanyIds = (task: TaskData) => {
  const { cleaningRequest, type } = task;
  const normalizedTaskType = normalizeType(type);
  const isSpaceCleanTask = normalizedTaskType === TypeEnum.Tom_Tasks_SpaceClean;
  const cleaningCompanyId = cleaningRequest?.company?.id;
  const companyIds =
    !isSpaceCleanTask && cleaningCompanyId ? [cleaningCompanyId] : [];
  return companyIds;
};
export const getTaskDuringStay = (task: TaskData) => !!get(task, "duringStay");
export const getTaskCheckedOutToday = (task: TaskData) =>
  !!get(task, "unit.checkInCheckOutInfo.checkOutToday");
export const getTaskCheckedInToday = (task: TaskData) =>
  !!get(task, "unit.checkInCheckOutInfo.checkInToday");
export const getTaskOccupiedAllDay = (task: TaskData) =>
  !!get(task, "unit.checkInCheckOutInfo.occupiedByToday");
export const getTaskPriority = ({ priority }: TaskData) => priority;
export const getTaskRecurrenceSettings = ({
  tomTaskRecurrenceSetting,
}: TaskData) => tomTaskRecurrenceSetting;
export const getTaskStatus = ({ status }: TaskData) => status;
export const getTaskDueBy = ({ dueAt }: TaskData) => dueAt;
export const getTaskSubjectType = ({ subjectType }: TaskData) =>
  subjectType as TaskSubjectType;
export const getTaskType = ({ type }: TaskData) => type;
export const getTaskOccupancyStatus = (task: TaskData) => {
  const now = moment();
  const isDuringStay = getTaskDuringStay(task);
  const isOccupiedAllDay = getTaskOccupiedAllDay(task);
  const checkInTodayTime = get(
    task,
    "unit.checkInCheckOutInfo.checkInToday.checkInTime",
  );
  const checkOutTodayTime = get(
    task,
    "unit.checkInCheckOutInfo.checkOutToday.checkOutTime",
  );
  const hasCheckedIn =
    checkInTodayTime && now.isSameOrAfter(moment(checkInTodayTime));
  const hasCheckedOut =
    checkOutTodayTime && now.isSameOrAfter(moment(checkOutTodayTime));
  if (
    isOccupiedAllDay ||
    (checkInTodayTime && hasCheckedIn) ||
    (checkOutTodayTime && !hasCheckedOut)
  ) {
    if (isDuringStay) {
      return OccupancyStatus[OccupancyState.DuringStay];
    }
    return OccupancyStatus[OccupancyState.Occupied];
  }
  return OccupancyStatus[OccupancyState.Vacant];
};
export const getRecurrOnDays = (task: TaskData): string[] =>
  get(task, "tomTaskRecurrenceSetting.daysOfWeek", []);
export const isPTETask = (task: TaskData) =>
  PTE_TASK_TYPES.some(type => task.type.includes(type));
export const mapTaskFeedItemToLastViewedAt = (task: TaskData) =>
  get(task, "taskFeedItemConnections.lastViewedAt", 0);
export const mapTaskFeedItemToUnread = (task: TaskData) =>
  get(task, "taskFeedItemConnections.unreadCount", 0);
export const isRecurring = (task: TaskData) =>
  !isEmpty(task.tomTaskRecurrenceSetting);

export const mapCheckoutStateToTag = (
  isCheckedInToday: boolean,
  isCheckedOutToday: boolean,
  isOccupiedAllDay: boolean,
) => {
  if (isOccupiedAllDay) return "Occupied all day";
  const states = [
    isCheckedInToday ? "Check In" : "",
    isCheckedOutToday ? "Check Out" : "",
  ].filter(x => !!x);
  if (!states.length) {
    return "Vacant";
  }
  return states.join(states.length > 1 ? " / " : "");
};

export const mapTaskDataToProps = (task: TaskData): Task => ({
  id: getTaskId(task),
  asanaUrl: getTaskAsanaUrl(task),
  assigneeIds: getTaskAssigneeIds(task),
  bookingInfo: getTaskBookingInfo(task),
  cityId: getTaskCityId(task),
  description: getTaskDescription(task),
  dueBy: getTaskDueBy(task),
  futureCheckouts: getTaskFutureCheckOuts(task),
  internalRef: getTaskInternalRef(task),
  isCheckedInToday: getTaskCheckedInToday(task),
  isCheckedOutToday: getTaskCheckedOutToday(task),
  isOccupiedAllDay: getTaskOccupiedAllDay(task),
  occupancyStatus: getTaskOccupancyStatus(task),
  priority: getTaskPriority(task),
  recurrenceSettings: getTaskRecurrenceSettings(task),
  scheduledStartAt: task.scheduledStartAt,
  status: getTaskStatus(task),
  subjectType: getTaskSubjectType(task),
  type: getTaskType(task),
  unreadCount: mapTaskFeedItemToUnread(task),
});
